import React, {
    createContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
    useContext,
    useRef,
    useMemo,
} from 'react';
import { UploadState } from '../types';
import { postHideUploadState } from '../utils/backendServices';
import { useOrganization, useUser } from '@clerk/clerk-react';

interface UploadContextType {
    uploadStateArray: UploadState[];
    triggerFetch: () => void;
    hideUploadState: (assetId: string) => Promise<void>;
    connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

interface WebSocketMessage {
    type: 'upload_states' | 'error' | 'heartbeat';
    data: any;
}

export const UploadContext = createContext<UploadContextType | undefined>(
    undefined,
);

interface UploadStateProviderProps {
    children: ReactNode;
}

export const UploadStateProvider: React.FC<UploadStateProviderProps> = ({
    children,
}) => {
    const [uploadStateArray, setUploadStateArray] = useState<UploadState[]>([]);
    const [connectionStatus, setConnectionStatus] = useState<
        'connecting' | 'connected' | 'disconnected' | 'error'
    >('disconnected');

    const {
        organization,
        isLoaded: isOrgLoaded,
        membership,
    } = useOrganization();
    const { user, isLoaded: isUserLoaded } = useUser();
    const websocketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Store auth data in refs to avoid triggering reconnections
    const authDataRef = useRef<{
        userId: string;
        orgId: string;
        orgRole: string;
    } | null>(null);

    // Store the stable IDs to detect when we actually need to reconnect
    const currentUserIdRef = useRef<string | null>(null);
    const currentOrgIdRef = useRef<string | null>(null);

    const connectWebSocket = useCallback(() => {
        const authData = authDataRef.current;
        if (!authData) {
            console.log('No auth data available for WebSocket connection');
            return;
        }

        // Close existing connection if any
        if (websocketRef.current) {
            console.log('Closing existing WebSocket connection');
            websocketRef.current.close();
        }

        console.log('Connecting WebSocket...');
        setConnectionStatus('connecting');

        // Create WebSocket connection
        const ws = new WebSocket('ws://localhost:8000/ws/upload_states');
        websocketRef.current = ws;

        ws.onopen = () => {
            console.log('WebSocket connected successfully');
            setConnectionStatus('connected');

            // Send authentication as first message
            const authMessage = {
                user_id: authData.userId,
                org_id: authData.orgId,
                org_role: authData.orgRole,
            };
            ws.send(JSON.stringify(authMessage));

            // Start heartbeat
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
            heartbeatIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'heartbeat', data: {} }));
                }
            }, 30000);
        };

        ws.onmessage = (event) => {
            try {
                const message: WebSocketMessage = JSON.parse(event.data);

                switch (message.type) {
                    case 'upload_states':
                        if (message.data && message.data.upload_states) {
                            setUploadStateArray(message.data.upload_states);
                        }
                        break;
                    case 'error':
                        console.error('WebSocket error:', message.data.message);
                        setConnectionStatus('error');
                        break;
                    case 'heartbeat':
                        // Heartbeat received, connection is alive
                        break;
                    default:
                        console.warn('Unknown message type:', message.type);
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        ws.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            setConnectionStatus('disconnected');

            // Clear heartbeat interval
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }

            // Only attempt to reconnect if we still have valid auth data
            // and the close wasn't intentional (code 1000 is normal closure)
            if (authDataRef.current && event.code !== 1000) {
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }
                reconnectTimeoutRef.current = setTimeout(() => {
                    console.log('Attempting to reconnect WebSocket...');
                    connectWebSocket();
                }, 3000);
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setConnectionStatus('error');
        };
    }, []); // No dependencies - function is stable

    // Update auth data and trigger connection when user/org data changes
    useEffect(() => {
        if (isOrgLoaded && isUserLoaded && organization && user) {
            const newUserId = user.id;
            const newOrgId = organization.id;
            const newOrgRole = membership?.role || 'org:member';

            // Check if this is new auth data that requires a reconnection
            const needsReconnection =
                currentUserIdRef.current !== newUserId ||
                currentOrgIdRef.current !== newOrgId;

            // Update the auth data
            authDataRef.current = {
                userId: newUserId,
                orgId: newOrgId,
                orgRole: newOrgRole,
            };

            currentUserIdRef.current = newUserId;
            currentOrgIdRef.current = newOrgId;

            if (needsReconnection || !websocketRef.current) {
                console.log(
                    'Auth data changed or no connection, connecting WebSocket...',
                );
                connectWebSocket();
            }
        } else {
            // Clear auth data if user/org not loaded
            authDataRef.current = null;
            currentUserIdRef.current = null;
            currentOrgIdRef.current = null;
        }
    }, [
        isOrgLoaded,
        isUserLoaded,
        organization?.id,
        user?.id,
        connectWebSocket,
    ]);

    // Cleanup on unmount only
    useEffect(() => {
        return () => {
            console.log(
                'UploadStateProvider unmounting, cleaning up WebSocket',
            );
            if (websocketRef.current) {
                websocketRef.current.close(1000, 'Component unmounting');
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, []);

    const triggerFetch = useCallback(() => {
        // For WebSocket, we can request fresh states if needed
        if (
            websocketRef.current &&
            websocketRef.current.readyState === WebSocket.OPEN
        ) {
            websocketRef.current.send(
                JSON.stringify({ type: 'get_states', data: {} }),
            );
        } else {
            // If WebSocket is not connected, try to reconnect
            connectWebSocket();
        }
    }, [connectWebSocket]);

    const hideUploadState = useCallback(
        async (assetId: string) => {
            // Optimistically update the UI
            const updatedStateArray = uploadStateArray.map((item) =>
                item.id === assetId
                    ? {
                          ...item,
                          display: false,
                      }
                    : item,
            );
            setUploadStateArray(updatedStateArray);

            try {
                // Still use the REST API for hiding upload state
                const newUploadStateArray = await postHideUploadState(assetId);
                setUploadStateArray(newUploadStateArray);
            } catch (error) {
                console.error('Error hiding upload state:', error);
                // Revert optimistic update on error
                setUploadStateArray(uploadStateArray);
            }
        },
        [uploadStateArray],
    );

    return (
        <UploadContext.Provider
            value={{
                uploadStateArray,
                triggerFetch,
                hideUploadState,
                connectionStatus,
            }}
        >
            {children}
        </UploadContext.Provider>
    );
};

export function useUploadContext() {
    const uploadContext = useContext(UploadContext);

    if (uploadContext === undefined) {
        throw new Error('useUploadContext must be used with an UploadContext');
    }

    return uploadContext;
}

export function useLatestSuccessfulUpload(): UploadState | null {
    const { uploadStateArray } = useUploadContext();
    const latestUploadRef = useRef<UploadState | null>(null);

    return useMemo(() => {
        // Find the latest successful upload without creating intermediate arrays
        let latestTime = latestUploadRef.current?.upload_time ?? -1;
        let newLatestUpload: UploadState | null = latestUploadRef.current;

        for (const upload of uploadStateArray) {
            if (
                upload.state === 'successful' &&
                upload.upload_time > latestTime
            ) {
                newLatestUpload = upload;
                latestTime = upload.upload_time;
            }
        }

        // Only update the ref if we found a different latest upload
        if (
            newLatestUpload?.id !== latestUploadRef.current?.id ||
            newLatestUpload?.upload_time !==
                latestUploadRef.current?.upload_time
        ) {
            latestUploadRef.current = newLatestUpload;
        }

        return latestUploadRef.current;
    }, [uploadStateArray]);
}
